import { IconBundleBlueprint } from '@backstage/plugin-app-react';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { SvgIcon, SvgIconProps } from '@material-ui/core';
import {
  ArgocdIcon,
  GithubIcon,
  GitlabIcon,
  GrafanaIcon,
  PostgresqlIcon,
} from './brandIcons';

/**
 * Icons registered under app-wide names, the way Backstage recommends: cards
 * ask for `refresh` or `external-link` through iconsApiRef and never import an
 * icon library themselves. Swapping icon sets later touches this file only.
 *
 * Paths are inlined rather than pulled from @material-ui/icons so that the rest
 * of the app can move off Material UI without dragging its icon package along.
 */

const RefreshIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4a8 8 0 1 0 7.73 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
  </SvgIcon>
);

const ExternalLinkIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
  </SvgIcon>
);

// Inherits the surrounding text colour, so the caller sets the meaning by
// wrapping it in a coloured <Text>.
const StatusDotIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="6" />
  </SvgIcon>
);

// The mark on the entry one is already on, in a menu that offers a choice. It
// is decorative: the row carries the meaning in `aria-checked`, and SvgIcon
// sets aria-hidden on its own as long as no titleAccess is passed.
const CheckIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </SvgIcon>
);

const LoginIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M11 7 9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-8v2h8v14z" />
  </SvgIcon>
);

const MonitoringIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V5h14v14zM7 12h2v5H7v-5zm4-5h2v10h-2V7zm4 3h2v7h-2v-7z" />
  </SvgIcon>
);

const PipelineIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M4 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm16 12a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM4 10a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm0 6a2 2 0 1 1 0 4 2 2 0 0 1 0-4zm4-10h5a3 3 0 0 1 3 3v6a3 3 0 0 0 3 3h-1v2h1a5 5 0 0 1-5-5V9a1 1 0 0 0-1-1H8V6z" />
  </SvgIcon>
);

// A shell prompt, `>_`: the chevron and the cursor bar are two subpaths of the
// same outline, so the glyph stays a single <path> like its neighbours.
const TerminalIcon = (props: SvgIconProps) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M4.4 6.2 3 7.6 7.4 12 3 16.4 4.4 17.8 10.2 12ZM12 16h8v2h-8z" />
  </SvgIcon>
);

export const iconsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    IconBundleBlueprint.make({
      name: 'waadoo-icons',
      params: {
        icons: {
          refresh: RefreshIcon,
          'external-link': ExternalLinkIcon,
          'status-dot': StatusDotIcon,
          check: CheckIcon,
          login: LoginIcon,
          monitoring: MonitoringIcon,
          pipeline: PipelineIcon,
          terminal: TerminalIcon,
          // Brand logos. Registering them here also makes them usable from a
          // catalog-info.yaml, where `metadata.links[].icon` resolves against
          // this very registry — that is how app1 links get the GitLab and
          // ArgoCD logos instead of the generic built-in glyphs.
          //
          // `github` deliberately shadows Backstage's own built-in icon of that
          // name. Not an accident and not a collision to rename away: the
          // environments card picks its forge logo by name, and the built-in
          // glyph would sit in a row next to the GitLab, ArgoCD and Grafana
          // marks without belonging to the same visual family.
          github: GithubIcon,
          gitlab: GitlabIcon,
          argocd: ArgocdIcon,
          grafana: GrafanaIcon,
          postgresql: PostgresqlIcon,
        },
      },
    }),
  ],
});
